<template>
	<teleport to="body">
		<div
			ref="modalRef"
			class="modal leftovers-modal"
			:class="{ show }"
			role="dialog"
			aria-modal="true"
			aria-labelledby="leftovers-modal-title"
			tabindex="-1"
			@keydown="handleDialogKeydown"
			v-if="meal">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 id="leftovers-modal-title" class="modal-title">Leftovers</h5>
						<button
							ref="closeButtonRef"
							@click="close"
							type="button"
							class="btn-close"
							aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<!-- Applied summary -->
						<div v-if="phase === 'summary'">
							<p class="text-muted mb-2">
								Nutrition totals show what was actually eaten. Use the
								"Original" toggle on the item to see the full meal.
							</p>
							<div class="leftovers-photos" v-if="meal.img_leftovers">
								<div class="leftovers-photo" v-if="meal.img">
									<small>Original</small>
									<img :src="getImageSource(meal.img)" alt="Original meal" />
								</div>
								<div class="leftovers-photo">
									<small>Leftovers</small>
									<img
										:src="getImageSource(meal.img_leftovers)"
										alt="Leftovers" />
								</div>
							</div>
							<ul
								class="list-unstyled leftover-fractions"
								v-if="appliedFractions.length">
								<li
									v-for="row in appliedFractions"
									:key="row.index ?? 'overall'">
									<span>{{ row.name || "Whole meal" }}</span>
									<span class="badge orange-badge">
										{{ eatenPercent(row.fraction_remaining) }}% eaten
									</span>
								</li>
							</ul>
							<div class="summary-actions">
								<button
									type="button"
									class="btn btn-lg btn-primary summary-primary-action"
									@click="startManual('summary')">
									<i class="bi bi-sliders" aria-hidden="true"></i>
									Adjust amounts
								</button>
								<details class="leftovers-more">
									<summary>More options</summary>
									<div class="leftovers-more-menu">
										<button type="button" @click="beginNewPhoto">
											<i class="bi bi-camera" aria-hidden="true"></i>
											Replace photo
										</button>
										<button
											type="button"
											class="remove-leftovers-action"
											@click="undoLeftovers">
											<i class="bi bi-trash" aria-hidden="true"></i>
											Remove leftovers
										</button>
									</div>
								</details>
							</div>
						</div>

						<!-- Durable background job -->
						<div v-else-if="phase === 'pending'" class="text-center my-3">
							<div class="spinner-border text-warning" role="status">
								<span class="visually-hidden">Processing leftovers</span>
							</div>
							<h6 class="mt-3">Leftovers are processing</h6>
							<p class="text-muted">
								The photo was uploaded successfully. You can close this dialog or
								the app; the meal will update when processing finishes.
							</p>
							<div class="action-row justify-content-center">
								<button
									type="button"
									class="btn btn-lg btn-outline-danger"
									@click="cancelPending">
									Cancel processing
								</button>
							</div>
						</div>

						<!-- Capture -->
						<div v-else-if="phase === 'capture'">
							<p class="text-muted mb-2">
								Snap the plate as it looks now. The AI compares it with the
								original photo and updates the meal in the background. Once the
								upload is accepted, you can close the app or turn off your phone.
							</p>
							<image-upload
								ref="uploadRef"
								v-model="preview"
								enable-targeting
								@targetChange="handleTarget"
								@targetEditorOpen="editorOpen = $event" />
							<div
								v-if="displayPreview && !editorOpen"
								class="image-preview">
								<img
									:src="displayPreview"
									alt="Leftovers preview"
									class="img-thumbnail mt-2" />
								<div class="mt-2">
									<button
										class="btn btn-sm btn-outline-warning"
										@click="changeTarget">
										<i class="bi bi-crosshair"></i>
										{{ target?.targetMode ? "Change target" : "Target item" }}
									</button>
								</div>
							</div>
							<div class="capture-actions" v-if="!editorOpen">
								<button
									class="btn btn-lg btn-primary submit-leftovers-action"
									:disabled="!preview"
									@click="analyze">
									Submit leftovers
								</button>
								<button
									type="button"
									class="manual-entry-action"
									@click="startManual('capture')"
									title="Skip the photo and set the amounts yourself">
									No photo? Enter amounts manually
								</button>
							</div>
						</div>

						<!-- Analyzing -->
						<div v-else-if="phase === 'analyzing'" class="text-center my-3">
							<div class="spinner-border text-warning" role="status"></div>
							<p class="mt-2">{{ statusMessage }}</p>
							<button
								class="btn btn-sm btn-outline-danger"
								@click="cancelAnalysis">
								Cancel
							</button>
						</div>

						<!-- Review -->
						<div v-else-if="phase === 'review'">
							<p class="text-muted mb-2">
								Adjust how much of each item was eaten, then apply.
							</p>
							<p v-if="isManualReview" class="manual-guidance">
								Move at least one slider to confirm the amounts. New manual
								entries start at 50% eaten.
							</p>
							<p v-if="notes" class="leftovers-notes">
								<i class="bi bi-stars"></i> {{ notes }}
							</p>
							<div
								class="leftover-row"
								v-for="row in reviewRows"
								:key="row.index ?? 'overall'">
								<div class="d-flex justify-content-between align-items-center">
									<label :for="rangeId(row)">
										<strong>{{ row.name || "Whole meal" }}</strong>
									</label>
									<output :for="rangeId(row)" aria-live="polite">
										{{ eatenPercent(row.fraction_remaining) }}% eaten
									</output>
								</div>
								<input
									:id="rangeId(row)"
									type="range"
									class="form-range"
									min="0"
									max="100"
									step="5"
									:value="eatenPercent(row.fraction_remaining)"
									:aria-valuetext="`${eatenPercent(row.fraction_remaining)}% eaten`"
									@input="updateFraction(row, $event)" />
							</div>
							<p class="review-totals" v-if="reviewCalories !== null">
								≈ {{ reviewCalories }} of {{ originalCalories }} kcal consumed
							</p>
							<div class="my-3 review-actions">
								<button
									class="btn btn-lg btn-outline-secondary"
									@click="backFromReview">
									Back
								</button>
								<button
									class="btn btn-lg btn-primary"
									:disabled="isManualReview && !manualDirty"
									@click="apply">
									Apply
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script setup>
	import { ref, computed, nextTick, watch, onBeforeUnmount } from "vue"
	import Swal from "sweetalert2"
	import { startLoading, stopLoading } from "@/utils/loaderBus"
	import { fetchWithAuth } from "@/utils/api-client"
	import getURL from "@/utils/url"
	import EventBus from "@/utils/eventBus"
	import { getImageSource } from "../utils/mealLib"
	import ImageUpload from "./ImageUpload.vue"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"

	const props = defineProps(["meal", "show"])
	const emit = defineEmits(["close"])

	// flow: capture -> analyzing -> review, or summary when leftovers were
	// already applied to this item
	const phase = ref("capture")
	const preview = ref(null)
	const target = ref(null)
	const editorOpen = ref(false)
	const uploadRef = ref(null)
	const statusMessage = ref("")
	const proposal = ref(null)
	const notes = ref(null)
	const reviewRows = ref([])
	const modalRef = ref(null)
	const closeButtonRef = ref(null)
	const manualDirty = ref(false)
	const reviewReturnPhase = ref("capture")
	const activeAnalysis = ref(null)
	const pendingJob = ref(null)
	let sse = null
	let backgroundSse = null
	let previouslyFocused = null
	let inertSiblings = []

	const isManualReview = computed(
		() => phase.value === "review" && !proposal.value
	)
	const hasUnsavedReview = computed(
		() => phase.value === "review" && (!!proposal.value || manualDirty.value)
	)
	const pendingWorkflowId = computed(
		() =>
			pendingJob.value?.workflowId ||
			props.meal?.leftovers_workflow_id ||
			null
	)

	const hasLeftovers = computed(() => {
		const entry = props.meal?.entry
		return !!(
			entry?.leftovers ||
			entry?.before_nutritional_info ||
			props.meal?.img_leftovers
		)
	})

	const displayPreview = computed(
		() => target.value?.targetImageSrc || preview.value
	)

	const appliedFractions = computed(() => {
		const entry = props.meal?.entry
		if (Array.isArray(entry?.leftovers?.fractions)) {
			return entry.leftovers.fractions
		}
		if (entry?.version === 2 && Array.isArray(entry.items)) {
			return entry.items
				.map((item, index) => ({
					index,
					name: item.name,
					fraction_remaining: item.leftover_fraction,
				}))
				.filter((row) => row.fraction_remaining != null)
		}
		return []
	})

	const originalCalories = computed(() => {
		const entry = props.meal?.entry
		if (!entry) return null
		if (entry.version === 2 && Array.isArray(entry.items)) {
			return Math.round(
				entry.items.reduce((sum, item) => {
					const original =
						item.original_nutritional_info || item.nutritional_info
					return sum + (Number(original?.calories) || 0)
				}, 0)
			)
		}
		const original = entry.before_nutritional_info || entry.nutritional_info
		return Math.round(Number(original?.calories) || 0)
	})

	const reviewCalories = computed(() => {
		const entry = props.meal?.entry
		if (!entry || !reviewRows.value.length) return null
		if (entry.version === 2 && Array.isArray(entry.items)) {
			return Math.round(
				reviewRows.value.reduce((sum, row) => {
					const item = entry.items[row.index]
					if (!item) return sum
					const original =
						item.original_nutritional_info || item.nutritional_info
					return (
						sum +
						(Number(original?.calories) || 0) * (1 - row.fraction_remaining)
					)
				}, 0)
			)
		}
		const overall = reviewRows.value[0]?.fraction_remaining ?? 0
		const original = entry.before_nutritional_info || entry.nutritional_info
		return Math.round((Number(original?.calories) || 0) * (1 - overall))
	})

	function clamp01(value) {
		const num = Number(value)
		if (!Number.isFinite(num)) return 0
		return Math.min(Math.max(num, 0), 1)
	}

	function eatenPercent(fractionRemaining) {
		return Math.round((1 - clamp01(fractionRemaining)) * 100)
	}

	async function close() {
		if (!(await confirmDiscard())) return
		await discardActiveAnalysis()
		resetState()
		emit("close")
	}

	function resetState() {
		closeStream()
		preview.value = null
		target.value = null
		editorOpen.value = false
		statusMessage.value = ""
		proposal.value = null
		notes.value = null
		reviewRows.value = []
		manualDirty.value = false
		reviewReturnPhase.value = "capture"
		phase.value = "capture"
	}

	function closeStream() {
		if (sse) {
			try {
				sse.close()
			} catch (_) {}
			sse = null
		}
	}

	function closeBackgroundStream() {
		if (backgroundSse) {
			try {
				backgroundSse.close()
			} catch (_) {}
			backgroundSse = null
		}
	}

	function handleTarget(t) {
		target.value = t
		preview.value = t?.imageSrc ?? null
	}

	function changeTarget() {
		uploadRef.value?.editTarget(target.value?.imageSrc || preview.value)
	}

	async function cancelAnalysis() {
		await discardActiveAnalysis()
		phase.value = "capture"
	}

	async function cancelPending() {
		const workflowId = pendingWorkflowId.value
		if (!workflowId) {
			phase.value = hasLeftovers.value ? "summary" : "capture"
			return
		}
		const confirmation = await Swal.fire({
			icon: "question",
			title: "Cancel leftovers processing?",
			text: "The uploaded photo and unfinished analysis will be removed.",
			showCancelButton: true,
			confirmButtonText: "Cancel processing",
			cancelButtonText: "Keep processing",
			reverseButtons: true,
		})
		if (!confirmation.isConfirmed) return

		startLoading()
		try {
			const response = await fetchWithAuth(`/api/leftovers/${props.meal.id}/cancel`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ workflow_id: workflowId }),
			})
			const data = await response.json()
			if (data.workflow_id) {
				pendingJob.value = { workflowId: data.workflow_id, cleanupFileUrls: [] }
				phase.value = "pending"
				monitorBackgroundStatus(data.workflow_id)
				return
			}
			if (data.error) throw new Error(data.error)
			closeBackgroundStream()
			pendingJob.value = null
			if (data.data) EventBus.emit("meal-updated", data.data)
			phase.value = hasLeftovers.value ? "summary" : "capture"
			// Hide the global loader before opening SweetAlert. Otherwise the loader
			// remains visible behind the success dialog until the user dismisses it.
			stopLoading()
			await Swal.fire({
				icon: "success",
				title: data.already_finished
					? "Leftovers already finished"
					: "Leftovers processing canceled",
			})
		} catch (error) {
			stopLoading()
			await Swal.fire({ icon: "error", title: error.message })
		} finally {
			stopLoading()
		}
	}

	async function fingerprintMealEntry() {
		function canonicalize(value) {
			if (Array.isArray(value)) return value.map(canonicalize)
			if (value && typeof value === "object") {
				return Object.keys(value)
					.sort()
					.reduce((result, key) => {
						result[key] = canonicalize(value[key])
						return result
					}, {})
			}
			return value
		}
		const bytes = new TextEncoder().encode(
			JSON.stringify(canonicalize(props.meal?.entry))
		)
		const digest = await crypto.subtle.digest("SHA-256", bytes)
		return [...new Uint8Array(digest)]
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("")
	}

	async function discardActiveAnalysis() {
		closeStream()
		const active = activeAnalysis.value
		activeAnalysis.value = null
		if (!active) return
		try {
			await fetchWithAuth(`/api/leftovers/${props.meal.id}/cancel`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					workflow_id: active.workflowId,
					cleanup_file_urls: active.cleanupFileUrls,
				}),
			})
		} catch (error) {
			console.log(error)
		}
	}

	async function confirmDiscard() {
		if (!hasUnsavedReview.value) return true
		const result = await Swal.fire({
			icon: "question",
			title: "Discard these leftovers amounts?",
			text: "Your reviewed amounts have not been applied.",
			showCancelButton: true,
			confirmButtonText: "Discard",
			cancelButtonText: "Keep editing",
			reverseButtons: true,
		})
		return result.isConfirmed
	}

	async function backFromReview() {
		if (!(await confirmDiscard())) return
		await discardActiveAnalysis()
		proposal.value = null
		notes.value = null
		reviewRows.value = []
		manualDirty.value = false
		phase.value =
			reviewReturnPhase.value === "summary" && hasLeftovers.value
				? "summary"
				: "capture"
		reviewReturnPhase.value = "capture"
	}

	async function beginNewPhoto() {
		await discardActiveAnalysis()
		phase.value = "capture"
	}

	async function analyze() {
		if (!preview.value) {
			Swal.fire({ icon: "error", title: "Take or upload a photo first." })
			return
		}

		phase.value = "analyzing"
		statusMessage.value = "Uploading photo..."

		try {
			const formData = new FormData()
			if (target.value?.targetMode) {
				formData.append("target_mode", target.value.targetMode)
			}
			if (target.value?.targetRegion) {
				formData.append(
					"target_region",
					JSON.stringify(target.value.targetRegion)
				)
			}

			async function appendImage(name, src) {
				const resp = await fetch(src)
				const blob = await resp.blob()
				const ext = (blob.type?.split("/")?.[1] || "png").split("+")[0]
				formData.append(name, blob, `${name}.${ext}`)
			}

			// masked target goes to the AI; the full photo is kept for display
			if (target.value?.targetImageSrc) {
				await appendImage("file", target.value.targetImageSrc)
				await appendImage("original_file", preview.value)
			} else {
				await appendImage("file", preview.value)
			}

			const response = await fetchWithAuth(
				`/api/leftovers/${props.meal.id}/analyze`,
				{
					method: "POST",
					body: formData,
				}
			)

			if (
				!(await SubscriptionHelper.requestCheckSubscription(
					response,
					"Leftovers"
				))
			) {
				phase.value = "capture"
				return
			}

			const data = await response.json()
			if (data.error) throw new Error(data.error)
			// The durable worker owns the files and final database update from here.
			// Closing the UI must not cancel or clean up this accepted job.
			activeAnalysis.value = null
			pendingJob.value = {
				workflowId: data.id,
				cleanupFileUrls: data.cleanup_file_urls || [],
			}
			if (data.pending_item) EventBus.emit("meal-updated", data.pending_item)
			resetState()
			emit("close")
			monitorBackgroundStatus(data.id)
			Swal.fire({
				icon: "success",
				title: "Leftovers submitted",
				text: "They will be applied in the background. You can safely close the app.",
				toast: true,
				position: "top-end",
				showConfirmButton: false,
				timer: 4000,
			})
		} catch (e) {
			console.log(e)
			phase.value = "capture"
			Swal.fire({ icon: "error", title: e.message })
		}
	}

	function monitorBackgroundStatus(workflowId) {
		closeBackgroundStream()
		const url = `${getURL("add-item", "")}status/stream?id=${encodeURIComponent(
			workflowId
		)}&wf=leftovers`
		backgroundSse = new EventSource(url, { withCredentials: false })

		backgroundSse.addEventListener("status", (evt) => {
			let payload = {}
			try {
				payload = JSON.parse(evt.data || "{}")
			} catch {
				return
			}

			const rawStatus = String(payload.status || "").toLowerCase()
			const terminal = ["complete", "completed", "errored", "error", "terminated"]
			if (!terminal.includes(rawStatus)) return

			closeBackgroundStream()

			let out = payload.output
			if (typeof out === "string") {
				try {
					out = JSON.parse(out)
				} catch (_) {}
			}

			if (
				(rawStatus === "complete" || rawStatus === "completed") &&
				out &&
				!out.error
			) {
				pendingJob.value = null
				if (out.updated_item) EventBus.emit("meal-updated", out.updated_item)
				if (props.show) phase.value = "summary"
				Swal.fire({
					icon: "success",
					title: "Leftovers applied",
					toast: true,
					position: "top-end",
					showConfirmButton: false,
					timer: 3000,
				})
			} else {
				pendingJob.value = null
				if (props.show) phase.value = hasLeftovers.value ? "summary" : "capture"
				Swal.fire({
					icon: "error",
					title: "Leftovers could not be applied",
					text: out?.error || payload.message || "Please submit the photo again.",
				})
			}
		})

		// our endpoint emits 'error' events with a payload on fatal failures;
		// plain network errors have no data and EventSource auto-reconnects
		backgroundSse.addEventListener("error", (evt) => {
			let payload = null
			try {
				payload = JSON.parse(evt.data || "null")
			} catch (_) {}
			if (payload?.message) {
				closeBackgroundStream()
				Swal.fire({
					icon: "error",
					title: "Leftovers status unavailable",
					text: payload.message,
				})
			}
		})
	}

	function startManual(returnPhase = "capture") {
		reviewReturnPhase.value = returnPhase
		const entry = props.meal?.entry
		if (entry?.version === 2 && Array.isArray(entry.items)) {
			reviewRows.value = entry.items.map((item, index) => ({
				index,
				name: item.name || `Item ${index + 1}`,
				fraction_remaining: clamp01(item.leftover_fraction ?? 0.5),
			}))
		} else {
			const existing = entry?.leftovers?.fractions?.[0]?.fraction_remaining
			reviewRows.value = [
				{
					index: null,
					name: "Whole meal",
					fraction_remaining: clamp01(existing ?? 0.5),
				},
			]
		}
		proposal.value = null
		notes.value = null
		manualDirty.value = false
		phase.value = "review"
	}

	function rangeId(row) {
		return `leftover-range-${props.meal?.id || "meal"}-${row.index ?? "overall"}`
	}

	function updateFraction(row, event) {
		row.fraction_remaining = 1 - Number(event.target.value) / 100
		manualDirty.value = true
	}

	async function apply() {
		if (!reviewRows.value.length) return

		startLoading()
		try {
			const body = { source: proposal.value ? "ai" : "manual" }
			body.entry_fingerprint =
				proposal.value?.entry_fingerprint || (await fingerprintMealEntry())
			const perItem = reviewRows.value.filter((row) =>
				Number.isInteger(row.index)
			)
			if (perItem.length) {
				body.fractions = perItem.map((row) => ({
					index: row.index,
					fraction_remaining: row.fraction_remaining,
				}))
			} else {
				body.overall = reviewRows.value[0]?.fraction_remaining ?? 0
			}

			const p = proposal.value
			if (p) {
				body.leftovers_file_url = p.leftovers_file_url
				body.leftovers_content_type = p.leftovers_content_type
				body.cleanup_file_urls = p.cleanup_file_urls
				if (p.proposal?.notes) body.notes = p.proposal.notes
				if (p.proposal?.empty_plate != null) {
					body.empty_plate = p.proposal.empty_plate
				}
			}

			const response = await fetchWithAuth(
				`/api/leftovers/${props.meal.id}/apply`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body),
				}
			)

			if (
				!(await SubscriptionHelper.requestCheckSubscription(
					response,
					"Leftovers"
				))
			) {
				return
			}

			const data = await response.json()
			if (data.error) throw new Error(data.error)

			EventBus.emit("meal-updated", data.data)
			activeAnalysis.value = null
			resetState()
			phase.value = "summary"
			stopLoading()
			await Swal.fire({ icon: "success", title: "Leftovers applied!" })
		} catch (e) {
			console.log(e)
			Swal.fire({ icon: "error", title: e.message })
		} finally {
			stopLoading()
		}
	}

	async function undoLeftovers() {
		const q = await Swal.fire({
			icon: "question",
			title: "Remove leftovers and restore the original nutrition?",
			showCancelButton: true,
			confirmButtonText: "Restore",
			cancelButtonText: "Cancel",
			reverseButtons: true,
		})
		if (!q.isConfirmed) return

		startLoading()
		try {
			const entryFingerprint = await fingerprintMealEntry()
			const response = await fetchWithAuth(`/api/leftovers/${props.meal.id}`, {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ entry_fingerprint: entryFingerprint }),
			})
			const data = await response.json()
			if (data.error) throw new Error(data.error)

			EventBus.emit("meal-updated", data.data)
			close()
			stopLoading()
			await Swal.fire({ icon: "success", title: "Original nutrition restored!" })
		} catch (e) {
			console.log(e)
			Swal.fire({ icon: "error", title: e.message })
		} finally {
			stopLoading()
		}
	}

	watch(
		() => props.show,
		async (shown) => {
			if (!shown) {
				await discardActiveAnalysis()
				restoreBackground()
				return
			}
			resetState()
			if (shown) {
				if (pendingWorkflowId.value) {
					phase.value = "pending"
					monitorBackgroundStatus(pendingWorkflowId.value)
				} else {
					phase.value = hasLeftovers.value ? "summary" : "capture"
				}
				previouslyFocused = document.activeElement
				await nextTick()
				isolateBackground()
				closeButtonRef.value?.focus()
			}
		}
	)

	function isolateBackground() {
		restoreBackground(false)
		const modal = modalRef.value
		if (!modal) return
		inertSiblings = [...document.body.children]
			.filter((element) => element !== modal)
			.map((element) => ({
				element,
				inert: element.inert,
				ariaHidden: element.getAttribute("aria-hidden"),
			}))
		for (const state of inertSiblings) {
			state.element.inert = true
			state.element.setAttribute("aria-hidden", "true")
		}
	}

	function restoreBackground(restoreFocus = true) {
		for (const state of inertSiblings) {
			state.element.inert = state.inert
			if (state.ariaHidden == null) state.element.removeAttribute("aria-hidden")
			else state.element.setAttribute("aria-hidden", state.ariaHidden)
		}
		inertSiblings = []
		if (restoreFocus && previouslyFocused?.focus) previouslyFocused.focus()
		if (restoreFocus) previouslyFocused = null
	}

	async function handleDialogKeydown(event) {
		if (event.key === "Escape") {
			event.preventDefault()
			await close()
			return
		}
		if (event.key !== "Tab") return
		const focusable = [...modalRef.value.querySelectorAll(
			'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
		)].filter((element) => !element.hidden && element.offsetParent !== null)
		if (!focusable.length) {
			event.preventDefault()
			modalRef.value.focus()
			return
		}
		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault()
			last.focus()
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault()
			first.focus()
		}
	}

	onBeforeUnmount(() => {
		closeStream()
		closeBackgroundStream()
		discardActiveAnalysis()
		restoreBackground()
	})
</script>

<style lang="less" scoped>
	//match the FoodDetailModal heading color
	div:first-child {
		--bs-heading-color: #ff6400;
		--bs-modal-color: #ff6400;
	}

	.modal {
		display: none; // Hidden by default
		position: fixed;
		z-index: 1060; // above FoodDetailModal (1050)
		top: 0;
		left: 0;
		width: 100%;
		height: 100dvh;
		padding: 1rem;
		box-sizing: border-box;
		overflow: hidden;
		outline: 0;
		background-color: rgba(0, 0, 0, 0.5); // Semi-transparent backdrop
		&.show {
			display: flex; // Center the dialog in the available viewport
			align-items: center;
			justify-content: center;
			overflow-y: auto;
		}
		.modal-dialog {
			position: relative;
			display: flex;
			align-items: center;
			width: min(100%, 500px);
			max-width: 500px;
			max-height: calc(100dvh - 2rem);
			margin: auto;
			pointer-events: none;
			.modal-content {
				position: relative;
				display: flex;
				flex-direction: column;
				width: 100%;
				max-height: calc(100dvh - 2rem);
				pointer-events: auto;
				background-color: #fff;
				border: 1px solid #dee2e6;
				border-radius: 0.3rem;
				outline: 0;
				.modal-header {
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 1rem;
					border-bottom: 1px solid #dee2e6;
					.modal-title {
						margin-bottom: 0;
						line-height: 1.5;
					}
					.btn-close {
						padding: 0.5rem;
						margin: -0.5rem -0.5rem -0.5rem auto;
					}
				}
				.modal-body {
					position: relative;
					flex: 1 1 auto;
					min-height: 0;
					padding: 1rem;
					overflow-y: auto;
					overscroll-behavior: contain;
					-webkit-overflow-scrolling: touch;
				}
			}
		}
	}

	@media (max-width: 575.98px) {
		.modal {
			padding:
				max(0.75rem, env(safe-area-inset-top))
				max(0.75rem, env(safe-area-inset-right))
				max(0.75rem, env(safe-area-inset-bottom))
				max(0.75rem, env(safe-area-inset-left));
		}

		.modal-dialog,
		.modal-content {
			max-height: calc(100dvh - 1.5rem);
		}

		.modal-header,
		.modal-body {
			padding: 0.875rem !important;
		}
	}

	@media (max-width: 480px) {
		:deep(.image-upload-buttons) {
			flex-direction: column;
			gap: 0.625rem;

			.btn {
				width: 100%;
				margin-right: 0 !important;
			}
		}
	}

	.action-row {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
	}

	.summary-actions {
		display: flex;
		align-items: stretch;
		gap: 0.75rem;
		margin: 1rem 0;

		.summary-primary-action {
			flex: 1;
			min-height: 3rem;
			border-radius: 0.75rem;
			font-weight: 700;
		}
	}

	.leftovers-more {
		position: relative;

		> summary {
			display: flex;
			align-items: center;
			height: 100%;
			padding: 0.75rem 1rem;
			cursor: pointer;
			user-select: none;
			list-style: none;
			border: 1px solid #ced4da;
			border-radius: 0.75rem;
			color: #495057;
			font-weight: 600;
			white-space: nowrap;

			&::-webkit-details-marker {
				display: none;
			}

			&::after {
				content: "\203A";
				margin-left: 0.5rem;
				font-size: 1.25rem;
				line-height: 1;
				transform: rotate(90deg);
				transition: transform 0.15s ease;
			}

			&:hover {
				background: #f8f9fa;
			}
		}

		&[open] > summary::after {
			transform: rotate(-90deg);
		}
	}

	.leftovers-more-menu {
		position: absolute;
		right: 0;
		top: calc(100% + 0.5rem);
		z-index: 3;
		width: max-content;
		min-width: 12.5rem;
		padding: 0.4rem;
		border: 1px solid #dee2e6;
		border-radius: 0.75rem;
		background: #fff;
		box-shadow: 0 0.75rem 2rem rgba(33, 37, 41, 0.16);

		button {
			display: flex;
			align-items: center;
			gap: 0.6rem;
			width: 100%;
			padding: 0.65rem 0.75rem;
			border: 0;
			border-radius: 0.5rem;
			background: transparent;
			color: #343a40;
			text-align: left;

			&:hover,
			&:focus-visible {
				background: #f1f3f5;
			}
		}

		.remove-leftovers-action {
			color: #c92a2a;
		}
	}

	.capture-actions {
		display: flex;
		flex-direction: column;
		gap: 0.35rem;
		margin: 1rem 0;

		.submit-leftovers-action {
			min-height: 3rem;
			border-radius: 0.75rem;
			font-weight: 700;
		}
	}

	.manual-entry-action {
		align-self: center;
		padding: 0.55rem 0.75rem;
		border: 0;
		background: transparent;
		color: #6c757d;
		font-weight: 600;
		text-decoration: underline;
		text-underline-offset: 0.2rem;

		&:hover,
		&:focus-visible {
			color: #ff6400;
		}
	}

	.review-actions {
		display: grid;
		grid-template-columns: 1fr 1.5fr;
		gap: 0.75rem;

		.btn {
			border-radius: 0.75rem;
			font-weight: 700;
		}
	}

	@media (max-width: 390px) {
		.summary-actions {
			flex-direction: column;
		}

		.leftovers-more > summary {
			justify-content: center;
		}
	}

	.leftovers-photos {
		display: flex;
		gap: 0.5rem;

		.leftovers-photo {
			flex: 1;
			min-width: 0;

			small {
				color: #ff6400;
				font-weight: 700;
			}

			img {
				width: 100%;
				border-radius: 8px;
			}
		}
	}

	.leftover-fractions li {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 0.5rem;
		padding: 0.15rem 0;
	}

	.leftover-row {
		margin-bottom: 0.75rem;
	}

	.leftovers-notes {
		color: #0ea5a9;
		font-weight: 600;
	}

	.manual-guidance {
		padding: 0.75rem;
		border-left: 4px solid #ffb020;
		background: #fff7e6;
		color: #5f4200;
	}

	.review-totals {
		font-weight: 700;
		color: #ff6400;
	}

	.orange-badge {
		background: #ff6400 !important;
		color: #fff !important;
		font-weight: 600;
		font-size: 0.95em;
	}

	.image-preview img {
		max-width: 100%;
	}
</style>
